import { AssignmentType } from '../../core/data-models/assignment-type.model';
import { LeaseStatus } from '../../core/data-models/lease-status.model';

/** The edit-lease form's values. Dates are `YYYY-MM-DD` strings ('' when empty), compared as strings. */
export interface LeaseFormValues {
  assignmentType: AssignmentType | null;
  leaseStatus: LeaseStatus | null;
  leaseStart: string;
  leaseEnd: string;
  leaseTermination: string;
}

/**
 * The lease status the dates imply, given the status picked in the form (`current`) and the one saved
 * on the assignment (`saved`). `today` is "YYYY-MM-DD".
 *  - A termination date on or before today makes it 'terminated'; an end date on or before today, 'expired'.
 *  - A lease that was never saved follows its start date: 'pending' before it, 'active' from it.
 *  - Otherwise a picked 'pending' / 'active' is kept, then a saved one, then the start date decides.
 */
export function deriveLeaseStatus(
  values: Pick<LeaseFormValues, 'leaseStart' | 'leaseEnd' | 'leaseTermination'>,
  current: LeaseStatus | null,
  saved: LeaseStatus | null,
  today: string,
): LeaseStatus {
  const { leaseStart, leaseEnd, leaseTermination } = values;
  if (leaseTermination && leaseTermination <= today) return 'terminated';
  if (leaseEnd && leaseEnd <= today) return 'expired';
  if (!saved && leaseStart) return leaseStart > today ? 'pending' : 'active';
  if (current === 'pending' || current === 'active') return current;
  if (saved === 'pending' || saved === 'active') return saved;
  return leaseStart && leaseStart <= today ? 'active' : 'pending';
}

/** True when the dates decide the status ('expired' / 'terminated'), so the admin can't pick one. */
export function isLeaseStatusFromDates(status: LeaseStatus | null): boolean {
  return status === 'expired' || status === 'terminated';
}

/** The first problem the hub would reject the lease for, or `null`. Messages match the hub's. */
export function leaseFormError(values: LeaseFormValues): string | null {
  if (!values.assignmentType) return 'Assignment type is required';
  if (values.assignmentType !== 'leased') return null;
  if (!values.leaseStatus) return 'Lease status is required for a leased assignment';
  if (!values.leaseStart || !values.leaseEnd) return 'Lease start and end dates are required for a leased assignment';
  if (values.leaseEnd < values.leaseStart) return 'Lease end date cannot be before the start date';
  if (values.leaseTermination && values.leaseTermination < values.leaseStart) {
    return 'Lease termination date cannot be before the start date';
  }
  return null;
}
